<template>
    <div class="row">
        <b-modal
            v-if="productToUnassign"
            v-model="showUnassignConfirmModal"
            title="Product unassign"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            no-close-on-backdrop
            @ok="unassignProduct(productToUnassign)"
        >
            <div>
                Do you want to unassign the product <b>"{{ productToUnassign.title }}"</b>?
            </div>
            <div class="mt-3">If you click <b>OK</b> there's no oopsies, no refunds, no come backs! I warned you!</div>
        </b-modal>

        <!-- Select product -->
        <div v-if="step === 'selectProduct'" class="col">
            <div v-if="!products || !products.length" class="text-center">
                <b-spinner variant="primary" class="my-2" />
            </div>
            <product-selector
                v-else
                :products="products"
                :selected-products="selectedProducts"
                @product-select="onProductSelected"
                @product-unassign="onProductUnassignClick"
            />
        </div>

        <!-- Select product file -->
        <div v-if="step === 'selectFile' && selectedProduct" class="col">
            <variants-configurator
                pod-service="printful"
                :selected-product-id="selectedProduct.id"
                :printful-files="printfulFiles"
                :store="store"
                :configurations="templateProductConfiguration.configurations"
                @variants-selected="onVariantsSelected"
            />
        </div>

        <!-- Final revision -->
        <div v-if="step === 'finalRevision' && selectedProduct" class="col">
            <revision-step
                :pod-service="'printful'"
                :product-title="selectedProduct.title"
                :provider-variants="selectedProviderVariants"
            />
            <b-btn variant="primary" class="float-right" @click="onSave">Save</b-btn>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { getProductsCatalog } from '@/api/storesApp/printful';
import ProductSelector from '../ProductSelector/ProductSelector.vue';
import type { File, FileWithSize, ProductClass } from '@/typings/printful/product';
import type { GenericPodProduct } from '../ProductSelector/typings';
import type { PodProviderConfiguration, TemplateProductConfiguration } from '../typings';
import { Ref, onBeforeMount, ref } from 'vue';
import VariantsConfigurator from '@/components/TemplateProductPodAssigner/VariantsConfigurator/VariantsConfigurator.vue';
import RevisionStep from '@/components/TemplateProductPodAssigner/RevisionStep/RevisionStep.vue';
import { Placeholder } from '../VariantsConfigurator/typings';
import { SelectedProviderVariants } from '@/typings/printify/printProvider';

const props = defineProps<{
    store: string;
    initialConfiguration: TemplateProductConfiguration;
}>();

const emit = defineEmits(['save']);

const showUnassignConfirmModal = ref(true);
const productToUnassign: Ref<GenericPodProduct | null> = ref(null);
const selectedProviderVariants: Ref<SelectedProviderVariants[]> = ref([]);
const printfulProducts: Ref<ProductClass[]> = ref([]);
const selectedProducts: Ref<GenericPodProduct[]> = ref([]);
const products: Ref<GenericPodProduct[]> = ref([]);
const step: Ref<'selectProduct' | 'selectFile' | 'finalRevision'> = ref('selectProduct');

const selectedProduct: Ref<GenericPodProduct | null> = ref(null);
let biggestPlaceholders: FileWithSize[] = [];
let printfulFiles: Ref<File[] | undefined> = ref([]);

const templateProductConfiguration: TemplateProductConfiguration = props.initialConfiguration;

onBeforeMount(() => {
    templateProductConfiguration.configurations = templateProductConfiguration.configurations.filter(
        (c) => c.provider === 'printful'
    );
    loadProducts();
});

const loadProducts = async () => {
    printfulProducts.value = await getProductsCatalog(props.store);
    products.value = printfulProducts.value.map((p) => ({
        id: p.id.toString(),
        title: p.title || p.model,
        image: p.image,
        description: p.description
    }));

    const ids = templateProductConfiguration.configurations.map((c) => c.productId);
    selectedProducts.value = products.value.filter((p) => ids.includes(p.id));
};

const onProductSelected = async (product: GenericPodProduct) => {
    selectedProduct.value = product;
    printfulFiles.value = printfulProducts.value.find((p) => p.id.toString() === product.id)?.files;

    if (!printfulFiles.value) {
        throw Error(`No files found for Printful product ${product.id}`);
    }
    step.value = 'selectFile';
};

const onProductUnassignClick = (product: GenericPodProduct) => {
    productToUnassign.value = product;
    showUnassignConfirmModal.value = true;
};

const unassignProduct = (product: GenericPodProduct | null) => {
    if (product) {
        // Remove any POD configuration belonging to the unassigned product
        templateProductConfiguration.configurations = templateProductConfiguration.configurations.filter(
            (c) => c.productId !== product.id
        );
        onSave();
    }
};

const onVariantsSelected = (value: {
    configurations: PodProviderConfiguration[];
    biggestPlaceholders: Placeholder[];
    selectedProviderVariants: SelectedProviderVariants[];
}) => {
    step.value = 'finalRevision';
    templateProductConfiguration.configurations = value.configurations;
    selectedProviderVariants.value = value.selectedProviderVariants;
    biggestPlaceholders = value.biggestPlaceholders.map(
        (pl): FileWithSize => ({
            file: pl.file!,
            width: pl.width,
            height: pl.height,
            dpi: pl.dpi ?? 300,
            aspectRatio: pl.aspectRatio ?? '',
            variantIds: []
        })
    );
};

const onSave = async () => {
    if (!selectedProduct.value || !biggestPlaceholders.length) {
        throw new Error('No product selected and trying to save configuration');
    }

    emit('save', {
        configuration: templateProductConfiguration,
        biggestPlaceholders: biggestPlaceholders.map((x) => {
            return {
                id: x.file.type,
                width: x.width,
                height: x.height,
                dpi: x.dpi,
                aspectRatio: x.aspectRatio
            };
        })
    });
};
</script>

<style lang="scss" scoped>
.variant-selector-wrapper::v-deep {
    .selector-title {
        color: white;
        background: linear-gradient(141deg, #7059e0 15%, #84c0d0 75%);
        padding: 0.5rem 1rem;
    }
}
</style>
